/**
 * Zod validation schemas for Stack Forward website
 */

import { z } from 'zod';

/**
 * Contact form validation schema
 */
export const contactFormSchema = z.object({
  name: z
    .string()
    .min(2, 'Name must be at least 2 characters')
    .max(80, 'Name must be less than 80 characters')
    .regex(/^[a-zA-Z\s\-'\.]+$/, 'Name contains invalid characters'),

  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address')
    .max(254, 'Email is too long'),

  phone: z
    .string()
    .max(30, 'Phone number is too long')
    .regex(/^[\+]?[0-9\s\-\(\)\.]+$/, 'Please enter a valid phone number')
    .optional()
    .or(z.literal('')),

  company: z
    .string()
    .max(120, 'Company name is too long')
    .optional()
    .or(z.literal('')),

  interest: z
    .array(z.string())
    .min(1, 'Please select at least one area of interest')
    .max(8, 'Too many interests selected'),

  message: z
    .string()
    .min(20, 'Message must be at least 20 characters')
    .max(2000, 'Message must be less than 2000 characters')
    .regex(/^[^<>]*$/, 'Message contains invalid characters'),

  consent: z
    .boolean()
    .refine(val => val === true, 'You must agree to the privacy policy'),
});

/**
 * Honeypot field validation (must be empty)
 */
export const honeypotSchema = z
  .string()
  .refine(val => val === '', 'Honeypot validation failed');

/**
 * Interest options validation
 */
export const interestOptionsSchema = z.enum([
  'erpnext',
  'odoo',
  'mattermost',
  'nextcloud',
  'keycloak',
  'gitlab',
  'minio',
  'other',
]);

/**
 * Type inference from schema
 */
export type ContactFormData = z.infer<typeof contactFormSchema>;

/**
 * Validation helper functions
 */
export const validateContactForm = (data: unknown) => {
  return contactFormSchema.safeParse(data);
};

export const validateHoneypot = (value: string) => {
  return honeypotSchema.safeParse(value);
};

/**
 * Get user-friendly error messages
 */
export function getFieldError(
  field: string,
  errors: z.ZodError
): string | undefined {
  const fieldError = errors.errors.find(err => err.path.includes(field));
  return fieldError?.message;
}

/**
 * Sanitize form input (only trim, don't modify spaces during typing)
 */
export function sanitizeInput(input: string): string {
  return input.trim();
}
